import React from "react"
import styled, { css } from "styled-components"
import { up } from "styled-breakpoints"
import colors from "@constants/colors"
import { timings, cubicBezier } from "@constants/animation"
import { body, bodyBold } from "@constants/typography"
import { Close } from "@components/icons"
import { Button as IconButton, IconWrapper } from "@components/icons/utils"
import Backdrop from "@components/Backdrop"
import Button from "@components/buttons/Button"
import PlainButton from "@components/buttons/PlainButton"
import { CountWrapper } from "@components/MenuItem/common"
import MenuItemAccordionCheckbox from "@components/MenuItemAccordionCheckbox"
import VerticalSpace from "@components/VerticalSpace"
import useModalBackgroundClose from "@hooks/useModalBackgroundClose"
import { toggleKeyInArray } from "@utils/index"
import { MobileOnly } from "@utils/MediaQueries"

const openProps = css`
  opacity: 1;
  transform: translateX(0);
  transition: opacity ${timings.quick} ${cubicBezier},
    transform ${timings.medium} ${cubicBezier};
`
const Container = styled.div<{ isOpen?: boolean }>`
  ${({ isOpen }) => css`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    flex-direction: column;
    display: flex;
    opacity: 0;
    overflow: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    transform: translateX(100%);
    transition: opacity ${timings.medium} ${cubicBezier},
      transform ${timings.quick} ${cubicBezier};
    will-change: opacity, transform;
    width: calc(100% - 40px);
    background-color: ${colors.snow};
    cursor: default;

    ${up("laptop")} {
      display: none;
    }

    ${isOpen ? openProps : ""}

    ${CountWrapper} {
      right: 44px;
    }
  `}
`

const HeaderContent = styled.div`
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  display: flex;
  height: 88px;
  padding: 20px;
`

const Header = styled.div`
  ${bodyBold}
  align-items: center;
  display: flex;
  color: ${colors.obsidian};

  ${IconButton} {
    width: auto;
    transform: translateX(10px);
  }

  ${IconWrapper} {
    transform: rotate(90deg);
    margin-right: 8px;
  }
`

const Body = styled.div`
  padding: 20px;
`

const Footer = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
  margin-top: 26px;
  padding: 20px;
  border-top: solid 1px ${colors.divider};
`

const ButtonWrapper = styled.div`
  width: calc(50% - 4px);
`

interface FiltersProps {
  isOpen?: boolean
  filters?: any
  activeFilters?: any
  onFilter?: (filters: any) => void
  onClearFilters?: () => void
  onRequestClose: () => void
}

const Filters: React.FC<FiltersProps> = ({
  isOpen,
  filters,
  activeFilters,
  onFilter,
  onClearFilters,
  onRequestClose,
}) => {
  const [selectedFilters, setSelectedFilters] = React.useState<any>([])

  React.useEffect(() => {
    setSelectedFilters(activeFilters)
  }, [activeFilters])

  const formattedFilters = React.useMemo(() => {
    const _formattedFilters = {}

    if (filters && Object.keys(filters).length > 0) {
      Object.keys(filters)
        .filter((key) => filters[key].length > 1)
        .forEach((key) => {
          _formattedFilters[key] = filters[key].map((f) => {
            const filter = typeof f === "string" ? f : f.name
            return {
              label: filter,
              checked:
                selectedFilters[key] &&
                selectedFilters[key].indexOf(filter) !== -1,
              onClick: () => {
                setSelectedFilters({
                  ...selectedFilters,
                  [key]: toggleKeyInArray(filter, selectedFilters[key]),
                })
              },
            }
          })
        })
    }
    return _formattedFilters
  }, [filters, activeFilters, selectedFilters])

  const handleClearFilters = () => {
    if (Object.keys(activeFilters).length > 0 && onClearFilters) {
      onClearFilters()
    } else {
      setSelectedFilters({})
    }
  }

  const menuRef: any = React.useRef(null)

  useModalBackgroundClose({
    modalRef: menuRef,
    isOpen,
    onRequestClose,
  })

  return (
    <MobileOnly>
      <Backdrop isOpen={isOpen}>
        <Container ref={menuRef} isOpen={isOpen}>
          <HeaderContent>
            <Header>Filter</Header>
            <IconButton onClick={onRequestClose}>
              <IconWrapper style={{ width: "30px", height: "30px" }}>
                <Close />
              </IconWrapper>
            </IconButton>
          </HeaderContent>
          <VerticalSpace height={0} mobileHeight={20} />
          <Body>
            {Object.keys(formattedFilters).length > 0 &&
              Object.keys(formattedFilters).map((key) => (
                <MenuItemAccordionCheckbox
                  key={key}
                  label={key}
                  items={formattedFilters[key]}
                  activeItemCount={
                    selectedFilters[key] ? selectedFilters[key].length : 0
                  }
                />
              ))}
            <VerticalSpace height={0} mobileHeight={20} />
          </Body>
          <Footer>
            <ButtonWrapper>
              <PlainButton onClick={handleClearFilters}>Clear</PlainButton>
            </ButtonWrapper>
            <ButtonWrapper>
              <Button
                onClick={() => {
                  if (onFilter) {
                    onFilter(selectedFilters)
                    onRequestClose()
                  }
                }}
              >
                Apply
              </Button>
            </ButtonWrapper>
          </Footer>
          <VerticalSpace height={0} mobileHeight={80} />
        </Container>
      </Backdrop>
    </MobileOnly>
  )
}

export default Filters
